import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    ShieldCheck, RefreshCcw, Search, CheckCircle2, Clock, AlertTriangle,
    HelpCircle, ArrowRight, X, Settings2, Info,
} from "lucide-react";
import { hasRole } from "@/lib/auth";

/**
 * VAULT · Insights · Data Freshness Centre
 *
 * Answers "How current is my VAULT information?" — never "how healthy
 * are my finances". The reviewed% is a record-review metric only.
 */

const STATE_TONE = {
    "Current":             "bg-emerald-50 text-emerald-800 border-emerald-200",
    "Review Soon":         "bg-amber-50 text-amber-900 border-amber-200",
    "Needs Review":        "bg-rose-50 text-rose-800 border-rose-200",
    "Missing Information": "bg-stone-100 text-stone-800 border-stone-300",
    "Never Confirmed":     "bg-blue-50 text-blue-800 border-blue-200",
    "Snoozed":             "bg-purple-50 text-purple-800 border-purple-200",
    "Ignored":             "bg-stone-100 text-stone-500 border-stone-300",
};

const STATE_ICON = {
    "Current":             CheckCircle2,
    "Review Soon":         Clock,
    "Needs Review":        AlertTriangle,
    "Missing Information": HelpCircle,
    "Never Confirmed":     Info,
};

function StatePill({ state }) {
    const Icon = STATE_ICON[state] || Info;
    return (
        <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full border ${STATE_TONE[state] || "bg-stone-100 text-stone-700 border-stone-200"}`}>
            <Icon size={10} /> {state}
        </span>
    );
}

function RulesDrawer({ open, onClose, rules, onSave }) {
    const [draft, setDraft] = useState(rules?.intervals || {});
    useEffect(() => { setDraft(rules?.intervals || {}); }, [rules]);
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center p-4"
             data-testid="freshness-rules-modal">
            <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden">
                <div className="bg-stone-900 text-white px-6 py-4 flex items-start justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-400 mb-1">Freshness Rules · Admin</div>
                        <div className="text-xl font-display">Recommended review intervals.</div>
                        <p className="text-[11.5px] text-stone-300 mt-1">
                            Configure how often each record type should be re-confirmed.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-stone-400 hover:text-white"><X size={16} /></button>
                </div>
                <div className="p-6 space-y-3 max-h-[60vh] overflow-y-auto">
                    {Object.entries(rules?.record_types || {}).map(([rt, meta]) => (
                        <div key={rt} className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <div className="text-[13px] font-semibold text-stone-900">{meta.label}</div>
                                <div className="text-[10.5px] text-stone-500">
                                    Required: {(meta.required_fields || []).join(", ") || "—"}
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <input type="number" min="7" max="1825"
                                       value={draft[rt] ?? ""}
                                       onChange={e => setDraft({...draft, [rt]: Number(e.target.value)})}
                                       className="w-24 border border-stone-300 rounded-lg px-2 py-1.5 text-sm tabular-nums text-right"
                                       data-testid={`freshness-rule-${rt}`} />
                                <span className="text-[11px] text-stone-500">days</span>
                            </div>
                        </div>
                    ))}
                </div>
                <div className="border-t border-stone-200 px-6 py-4 bg-stone-50 flex items-center justify-end gap-2">
                    <button onClick={onClose} className="px-4 py-2 rounded-full border border-stone-300 text-[13px] font-semibold text-stone-700 hover:border-stone-500">
                        Cancel
                    </button>
                    <button onClick={() => onSave(draft)}
                            className="px-5 py-2 rounded-full bg-amber-500 text-stone-900 text-[13px] font-bold hover:bg-amber-400"
                            data-testid="freshness-rules-save">
                        Save rules
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function DataFreshness() {
    const [overview, setOverview] = useState(null);
    const [queue, setQueue] = useState({ rows: [], total: 0 });
    const [rules, setRules] = useState(null);
    const [showRules, setShowRules] = useState(false);
    const [filter, setFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const canAdmin = hasRole("admin");

    const load = async () => {
        try {
            const [ov, q, r] = await Promise.all([
                api.get("/data-freshness/overview"),
                api.get("/data-freshness/queue", { params: { limit: 200, ...(typeFilter ? {record_type: typeFilter} : {}) } }),
                api.get("/data-freshness/rules"),
            ]);
            setOverview(ov.data); setQueue(q.data); setRules(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load Data Freshness");
        }
    };
    useEffect(() => { load(); }, [typeFilter]);

    const act = async (row, kind, until = null) => {
        try {
            await api.post(`/data-freshness/${kind}`, {
                record_type: row.record_type, record_id: row.record_id,
                until,
            });
            toast.success({ confirm: "Marked current", snooze: "Snoozed", ignore: "Ignored" }[kind]);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update");
        }
    };

    const saveRules = async (intervals) => {
        try {
            await api.patch("/data-freshness/rules", { intervals });
            toast.success("Rules updated");
            setShowRules(false); load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't save rules");
        }
    };

    const filtered = queue.rows.filter(r =>
        !filter || (r.display_name || "").toLowerCase().includes(filter.toLowerCase())
        || (r.record_type || "").toLowerCase().includes(filter.toLowerCase())
    );

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="data-freshness">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <ShieldCheck size={12} /> Insights · Data Freshness
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Is your VAULT current?
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        This measures record review and completeness only — not wealth or financial health.
                        Confirm records still current, snooze if not needed yet, or edit details that are missing.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-stone-300 text-[13px] font-semibold text-stone-700 hover:border-stone-500"
                            data-testid="freshness-refresh">
                        <RefreshCcw size={12} /> Refresh
                    </button>
                    {canAdmin && (
                        <button onClick={() => setShowRules(true)}
                                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-stone-900 text-white text-[13px] font-semibold hover:bg-stone-700"
                                data-testid="freshness-configure">
                            <Settings2 size={12} /> Configure rules
                        </button>
                    )}
                </div>
            </div>

            {/* Reviewed % KPI */}
            {overview && (
                <div className="bg-white border border-stone-200 rounded-2xl p-6 mb-6 grid grid-cols-1 md:grid-cols-5 gap-6"
                     data-testid="freshness-kpi">
                    <div className="md:col-span-1">
                        <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold mb-2">Your VAULT is</div>
                        <div className="font-display text-5xl text-stone-900 leading-none tabular-nums">
                            {overview.reviewed_pct}<span className="text-2xl text-stone-500">%</span>
                        </div>
                        <div className="text-sm text-stone-500 mt-1">reviewed</div>
                    </div>
                    <div className="md:col-span-4 grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Chip label="Current records" value={overview.current} tone="emerald" />
                        <Chip label="Needs review"    value={overview.needs_review} tone="rose" />
                        <Chip label="Missing info"    value={overview.missing} tone="stone" />
                        <Chip label="Recently updated" value={overview.recently_updated} tone="blue" />
                    </div>
                    <div className="md:col-span-5 text-[11.5px] text-stone-500 border-t border-stone-100 pt-3 flex items-start gap-2">
                        <Info size={12} className="mt-0.5 shrink-0" />
                        <span>{overview.explanation}</span>
                    </div>
                </div>
            )}

            {/* Per-type breakdown */}
            {overview && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
                    <button onClick={() => setTypeFilter("")}
                            className={`text-left rounded-xl border p-3 hover:border-stone-500 ${typeFilter === "" ? "border-stone-900 bg-stone-50" : "border-stone-200 bg-white"}`}
                            data-testid="freshness-type-all">
                        <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">All</div>
                        <div className="text-2xl font-display tabular-nums">{overview.grand_total}</div>
                    </button>
                    {Object.entries(overview.per_type).map(([rt, v]) => (
                        <button key={rt} onClick={() => setTypeFilter(rt)}
                                className={`text-left rounded-xl border p-3 hover:border-stone-500 ${typeFilter === rt ? "border-stone-900 bg-stone-50" : "border-stone-200 bg-white"}`}
                                data-testid={`freshness-type-${rt}`}>
                            <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold truncate">{v.label}</div>
                            <div className="text-2xl font-display tabular-nums">
                                {v.total === 0 || v.reviewed_pct == null ? <span className="text-stone-300">—</span> : (
                                    <>{v.reviewed_pct}<span className="text-xs text-stone-400">%</span></>
                                )}
                            </div>
                            <div className="text-[10.5px] text-stone-500 mt-0.5">{v.total} records · {v.interval_days}d</div>
                        </button>
                    ))}
                </div>
            )}

            {/* Review Queue */}
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden mb-6"
                 data-testid="freshness-queue">
                <div className="px-4 py-3 border-b border-stone-100 flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-display text-stone-900">Review My VAULT</h2>
                        <span className="text-[11px] text-stone-500">{queue.total} records to review</span>
                    </div>
                    <div className="relative">
                        <Search size={12} className="absolute left-2.5 top-2.5 text-stone-400" />
                        <input placeholder="Filter…" value={filter} onChange={e => setFilter(e.target.value)}
                               className="pl-7 pr-3 py-1.5 border border-stone-300 rounded-full text-[13px] w-56"
                               data-testid="freshness-filter" />
                    </div>
                </div>
                {filtered.length === 0 ? (
                    <div className="p-10 text-center text-sm text-stone-400" data-testid="freshness-empty">
                        Nothing needs review right now. Your VAULT looks current.
                    </div>
                ) : filtered.map(row => (
                    <div key={`${row.record_type}-${row.record_id}`}
                         className="border-b border-stone-100 last:border-0 px-4 py-3 flex items-center gap-3 hover:bg-stone-50"
                         data-testid={`freshness-row-${row.record_type}-${row.record_id}`}>
                        <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                                <StatePill state={row.state} />
                                <span className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">
                                    {row.label}
                                </span>
                                {row.source && (
                                    <span className="text-[10.5px] text-stone-500">
                                        · Source: {row.source}
                                    </span>
                                )}
                            </div>
                            <div className="text-[13.5px] font-semibold text-stone-900 truncate mt-1">
                                {row.display_name}
                            </div>
                            {row.missing_fields && row.missing_fields.length > 0 && (
                                <div className="text-[11px] text-rose-800 mt-0.5">
                                    Missing: {row.missing_fields.join(", ")}
                                    <span className="text-stone-500 ml-1">
                                        · Confirm is hidden until these are filled — use Edit to add them.
                                    </span>
                                </div>
                            )}
                            {row.days_since != null && (
                                <div className="text-[10.5px] text-stone-500 mt-0.5">
                                    Last touched {row.days_since}d ago
                                </div>
                            )}
                        </div>
                        <div className="flex gap-1 shrink-0 items-center flex-wrap">
                            {row.state !== "Missing Information" && (
                                <button onClick={() => act(row, "confirm")}
                                        className="text-[11px] font-semibold text-emerald-800 hover:text-emerald-900 px-2.5 py-1 rounded border border-emerald-200"
                                        data-testid={`freshness-confirm-${row.record_type}-${row.record_id}`}>
                                    Confirm current
                                </button>
                            )}
                            <Link to={row.route}
                                  className="text-[11px] font-semibold text-stone-700 hover:text-stone-900 px-2.5 py-1 rounded border border-stone-200 inline-flex items-center gap-1"
                                  data-testid={`freshness-edit-${row.record_type}-${row.record_id}`}>
                                Edit <ArrowRight size={10} />
                            </Link>
                            <label className="text-[11px] font-semibold text-stone-500 hover:text-stone-800 px-2 py-1 rounded border border-stone-200 inline-flex items-center gap-1 cursor-pointer relative"
                                   onClick={e => {
                                       // Guard against the click bubbling to the sr-only
                                       // <input> and re-opening the picker twice.
                                       if (e.target.tagName === "INPUT") return;
                                       e.preventDefault();
                                       const inp = e.currentTarget.querySelector("input[type=date]");
                                       if (inp && typeof inp.showPicker === "function") {
                                           try { inp.showPicker(); } catch { /* older browsers */ }
                                       }
                                   }}
                                   data-testid={`freshness-snooze-${row.record_type}-${row.record_id}`}>
                                Snooze
                                <input type="date"
                                       aria-label="Snooze until"
                                       className="sr-only"
                                       onChange={e => e.target.value && act(row, "snooze", e.target.value)} />
                            </label>
                            {canAdmin && (
                                <button onClick={() => act(row, "ignore")}
                                        className="text-[11px] font-semibold text-stone-600 hover:text-rose-700 px-2.5 py-1 rounded border border-stone-300"
                                        data-testid={`freshness-ignore-${row.record_type}-${row.record_id}`}>
                                    Ignore
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <RulesDrawer open={showRules} onClose={() => setShowRules(false)}
                         rules={rules} onSave={saveRules} />
        </div>
    );
}

const TONE_CLS = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    rose:    "bg-rose-50 border-rose-200 text-rose-900",
    stone:   "bg-stone-100 border-stone-200 text-stone-800",
    blue:    "bg-blue-50 border-blue-200 text-blue-900",
};

function Chip({ label, value, tone }) {
    return (
        <div className={`rounded-xl border p-3 ${TONE_CLS[tone] || TONE_CLS.stone}`}>
            <div className="text-[10.5px] uppercase tracking-widest font-semibold opacity-80">{label}</div>
            <div className="text-2xl font-display tabular-nums mt-1">{value}</div>
        </div>
    );
}
